import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { CreateModule } from './CreateModule';
import { FaChevronDown, FaChevronRight, FaFolder, FaPen } from 'react-icons/fa';
import { IModuleSchema, ModuleService } from '@/api/module';
import { IProjectSchema } from '@/api/project';
import { Loading } from '@/components/Loading';
import { CreateSubmodule } from './CreateSubmodule';
import { DeleteModule } from './DeleteModule';
import { CloneModule } from './CloneModule';
import classNames from 'classnames';
import { IoSearch } from 'react-icons/io5';
import { ITestRunSchema } from '@/api/testRun';
import { useLocation } from 'react-router-dom';
import { EditModule } from './EditModule';

type IModuleContext = {
  moduleRefetch?: () => void;
  selectedModule?: IModuleSchema;
  setSelectedModule?: React.Dispatch<IModuleSchema>;
  checked?: IModuleSchema[];
  setChecked?: React.Dispatch<React.SetStateAction<IModuleSchema[]>>;
  isViewer?: boolean;
  isTestRun?: boolean;
  showCheckbox?: boolean;
};

type TestCaseModuleProps = {
  isViewer?: boolean;
  showCheckbox?: boolean;
  project: IProjectSchema;
  testRun?: ITestRunSchema;
  onSelectedModule: (module: IModuleSchema) => void;
  onCheckedModule?: (modules: IModuleSchema[]) => void;
  checkedModules?: IModuleSchema[];
  showCheckedOnly?: boolean;
  isArchive?: boolean;
  selectedShareModule?: boolean;
  setModuleRefetch?: React.Dispatch<React.SetStateAction<() => void>>;
};

type ModuleProps = {
  module: IModuleSchema;
  isChecked?: boolean;
};

const ModuleContext = createContext<IModuleContext>({});

const Module: React.FC<ModuleProps> = ({ module, isChecked }) => {
  const {
    isViewer,
    showCheckbox,
    selectedModule,
    setSelectedModule,
    moduleRefetch,
    checked,
    setChecked,
    isTestRun,
  } = useContext(ModuleContext);

  const [isShow, setIsShow] = useState(false);

  const isAllModule = module.module_name === 'All Module';

  const handleCheckboxChange = (module: IModuleSchema, add: boolean) => {
    const traverseModules = (mod: IModuleSchema): IModuleSchema[] => {
      let modules = [mod];
      if (mod.submodule && mod.submodule.length > 0) {
        mod.submodule.forEach(sub => {
          modules = modules.concat(traverseModules(sub));
        });
      }
      return modules;
    };

    const relatedModules = traverseModules(module);

    if (add) {
      // Add all related modules (parent + submodules)
      setChecked?.(prev => [
        ...prev,
        ...relatedModules.filter(m => !prev.some(e => e.id === m.id)),
      ]);
    } else {
      // Remove all related modules (parent + submodules)
      setChecked?.(prev =>
        prev.filter(m => !relatedModules.some(e => e.id === m.id)),
      );
    }
  };

  return (
    <div className="flex flex-col">
      <div
        className={classNames('flex flex-row gap-2', {
          'bg-slate-100 rounded': module.id == selectedModule?.id,
        })}
      >
        <div className="flex flex-col flex-grow">
          <div className="flex flex-row gap-2 p-1 ">
            <button
              type="button"
              className="flex flex-row items-center flex-grow gap-2"
              onClick={() => {
                setSelectedModule?.(module);
                setIsShow(prev => !prev);
              }}
            >
              {isShow ? (
                <FaChevronDown className="w-3 h-3" />
              ) : (
                <FaChevronRight className="w-3 h-3" />
              )}
              <FaFolder className="text-blue-400" />
              <span className="text-sm text-left select-none">
                {module.module_name}
                <span className="text-xs text-gray-500">
                  {' '}
                  (
                  {isTestRun
                    ? module.total_test_run_detail
                    : module.total_test_case}
                  )
                </span>
              </span>
            </button>
          </div>
        </div>

        {/* Tombol CreateSubmodule, EditModule, CloneModule, dan DeleteModule tidak muncul jika module_name === "All Module" */}
        {/* Tombol CreateSubmodule dan CloneModule tidak muncul jika is_archive === true */}
        {!isViewer && !module.is_archive && !isAllModule && (
          <>
            <CreateSubmodule
              module={module}
              onSuccess={() => moduleRefetch?.()}
            />
            <EditModule
              module={module}
              onSuccess={() => {
                moduleRefetch?.();
              }}
              triggerButton={
                <button type="button">
                  <FaPen />
                </button>
              }
            />
            <CloneModule
              module={module}
              onSuccess={() => moduleRefetch?.()}
              triggerButtonType="module"
            />
          </>
        )}

        {!isViewer && !isAllModule && (
          <DeleteModule
            id={module.id!}
            isArchive={module.is_archive ?? false}
            onSuccess={() => moduleRefetch?.()}
            module={module}
          />
        )}

        {showCheckbox && (
          <input
            type="checkbox"
            checked={checked?.some(e => e.id === module.id) || isChecked}
            onChange={e => {
              handleCheckboxChange(module, e.target.checked);
            }}
          />
        )}
      </div>

      <div className="flex flex-col w-full pl-2">
        {isShow &&
          module.submodule &&
          module.submodule.length > 0 &&
          module.submodule.map(e => {
            return (
              <Module
                module={e}
                key={e.id}
                isChecked={checked?.some(m => m.id === e.id)}
              />
            );
          })}
      </div>
    </div>
  );
};

export const TestCaseModule: React.FC<TestCaseModuleProps> = ({
  isViewer,
  showCheckbox,
  project,
  testRun,
  onSelectedModule,
  onCheckedModule,
  checkedModules = [],
  showCheckedOnly,
  isArchive,
  selectedShareModule = false,
  setModuleRefetch,
}) => {
  const location = useLocation();

  const {
    data: moduleData,
    isFetching: isModuleFetching,
    refetch: moduleRefetch,
  } = ModuleService.useGetAll({
    projectId: project.id,
    testRunId: testRun?.id,
    isArchive,
  });

  const [tempSearch, setTempSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState<IModuleSchema>();
  const [checked, setChecked] = useState<IModuleSchema[]>(checkedModules);

  useEffect(() => {
    if (selectedModule) onSelectedModule(selectedModule);
  }, [selectedModule]);

  useEffect(() => {
    onCheckedModule?.(checked);
  }, [checked]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const moduleIdFromUrl = params.get('moduleId');
    if (moduleIdFromUrl) {
      const module = moduleData?.find(m => m.id === moduleIdFromUrl);
      if (module) {
        setSelectedModule(module);
      }
    }
  }, [location.search, moduleData]);

  useEffect(() => {
    if (setModuleRefetch) {
      setModuleRefetch(() => moduleRefetch);
    }
  }, [moduleRefetch, setModuleRefetch]);

  const findModuleAndPreserveHierarchy = (
    modules: IModuleSchema[],
    onFilter: (module: IModuleSchema) => boolean,
  ) => {
    const resultArray = [];

    for (const module of modules) {
      // Buat salinan module agar kita bisa mengedit tanpa mengubah data asli
      const newModule = { ...module, submodule: [] as IModuleSchema[] };

      if (onFilter?.(module)) {
        // Jika module_name cocok, masukkan module tersebut
        newModule.submodule = module.submodule ?? []; // Pertahankan submodule aslinya
        resultArray.push(newModule);
      } else if (module.submodule && module.submodule.length > 0) {
        // Jika ada submodule, cari di dalam submodule secara rekursif
        const submoduleResult = findModuleAndPreserveHierarchy(
          module.submodule,
          onFilter.bind(module),
        );

        if (submoduleResult.length > 0) {
          // Jika submodule ditemukan, tambahkan submodule yang cocok ke dalam module
          newModule.submodule = submoduleResult;
          resultArray.push(newModule); // Hanya tambahkan module jika ada submodule yang relevan
        }
      }
    }

    return resultArray; // Kembalikan hasil pencarian
  };

  // Fungsi untuk memfilter modul berdasarkan selectedModule untuk share module
  const filterModules = (
    modules: IModuleSchema[],
    onFilter: (module: IModuleSchema) => boolean,
  ) => {
    const resultArray = [];

    for (const module of modules) {
      const newModule = { ...module, submodule: [] as IModuleSchema[] };

      if (onFilter?.(module)) {
        newModule.submodule = module.submodule ?? [];
        resultArray.push(newModule);
      } else if (module.submodule && module.submodule.length > 0) {
        const submoduleResult = filterModules(module.submodule, onFilter);

        if (submoduleResult.length > 0) {
          newModule.submodule = submoduleResult;
          resultArray.push(newModule);
        }
      }
    }

    return resultArray;
  };

  const modules = useMemo(() => {
    let result: IModuleSchema[] = moduleData ?? [];

    if (selectedShareModule && selectedModule?.module_name) {
      result = filterModules(
        result,
        module => module.module_name === selectedModule.module_name,
      );
    } else {
      if (showCheckedOnly) {
        result = findModuleAndPreserveHierarchy(
          result,
          module => !checkedModules.find(e => e.id === module.id),
        );
      }

      if (searchQuery) {
        result = findModuleAndPreserveHierarchy(result, module =>
          module.module_name.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      }
    }

    result = [...result];

    return result;
  }, [
    moduleData,
    searchQuery,
    checkedModules,
    selectedModule,
    selectedShareModule,
  ]);

  const isModuleChecked = (module: IModuleSchema) => {
    return checkedModules.some(checkedModule => checkedModule.id === module.id);
  };

  const contextValue = useMemo(
    () => ({
      selectedModule,
      setSelectedModule,
      moduleRefetch,
      checked,
      setChecked,
      isViewer,
      showCheckbox,
      isTestRun: testRun !== undefined,
    }),
    [
      selectedModule,
      setSelectedModule,
      moduleRefetch,
      checked,
      setChecked,
      isViewer,
      showCheckbox,
      testRun,
    ],
  );

  return (
    <ModuleContext.Provider value={contextValue}>
      <div className="flex flex-col h-screen p-4 bg-white border rounded shadow-lg">
        <div className="flex flex-row justify-between">
          <span className="font-bold">Epic</span>
          {!isViewer ? (
            <CreateModule project={project} onSuccess={moduleRefetch} />
          ) : (
            <></>
          )}
        </div>
        <div className="flex-row hidden w-full border border-gray-100 rounded shadow-sm md:flex as-2 justify-self-end">
          <input
            type="text"
            className="w-full p-1 px-2 bg-gray-100 border-0 rounded-l focus:ring-0"
            placeholder="Search Epic"
            onKeyDown={e => {
              if (e.code === 'Enter') {
                e.preventDefault();
                setSearchQuery(tempSearch); // Set pencarian saat tekan Enter
              }
            }}
            onChange={e => setTempSearch(e.target.value)}
          />
          <button
            type="button"
            className="flex items-center p-2 px-2 bg-gray-100"
            // onClick={() => setSearchQuery(searchQuery)} // set searchQuery when button is clicked
            onClick={() => setSearchQuery(tempSearch)} // set searchQuery when button is clicked
          >
            <IoSearch size={18} className="text-gray-alurkerja-1" />
          </button>
        </div>

        <div className="flex flex-col pr-3 mt-4 overflow-y-scroll">
          {isModuleFetching ? (
            <Loading />
          ) : (
            <div className="flex flex-col gap-1 ">
              {modules?.map(module => (
                <Module
                  key={module.id}
                  module={module}
                  isChecked={isModuleChecked(module)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </ModuleContext.Provider>
  );
};
